import React, { useState, useMemo } from 'react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import { 
  ArrowLeft, 
  Calendar, 
  Grid, 
  Trash2, 
  ChevronRight, 
  Edit2, 
  Check, 
  X,
  Store,
  Users,
  Zap,
  Droplets,
  Wifi,
  Printer,
  Megaphone,
  Car,
  Fuel,
  Utensils,
  Wrench,
  Vote,
  Landmark,
  Shield,
  Sparkles,
  Truck,
  Laptop,
  Armchair,
  MoreHorizontal,
  Plus
} from 'lucide-react';
import AddNotesModal from './AddNotesModal';
import billService from '../../services/billService';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount || 0);
};

const formatDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear().toString().substring(2)}`;
};

const formatLocalDate = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  const pad = (num) => String(num).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const EXPENSE_CATEGORIES = [
  { key: LangKeys.cat_rent, icon: <Store size={20} />, label: "Rent" },
  { key: LangKeys.cat_salaries, icon: <Users size={20} />, label: "Salaries" },
  { key: LangKeys.cat_electricity, icon: <Zap size={20} />, label: "Electricity" },
  { key: LangKeys.cat_water, icon: <Droplets size={20} />, label: "Water" },
  { key: LangKeys.cat_internet, icon: <Wifi size={20} />, label: "Internet" },
  { key: LangKeys.cat_office, icon: <Printer size={20} />, label: "Office Stationery" },
  { key: LangKeys.cat_marketing, icon: <Megaphone size={20} />, label: "Marketing" },
  { key: LangKeys.cat_travel, icon: <Car size={20} />, label: "Travel" },
  { key: LangKeys.cat_fuel, icon: <Fuel size={20} />, label: "Fuel" },
  { key: LangKeys.cat_food, icon: <Utensils size={20} />, label: "Food & Drinks" },
  { key: LangKeys.cat_repairs, icon: <Wrench size={20} />, label: "Repairs" },
  { key: LangKeys.cat_taxes, icon: <Landmark size={20} />, label: "Taxes" },
  { key: LangKeys.cat_insurance, icon: <Shield size={20} />, label: "Insurance" },
  { key: LangKeys.cat_cleaning, icon: <Sparkles size={20} />, label: "Cleaning" },
  { key: LangKeys.cat_delivery, icon: <Truck size={20} />, label: "Delivery" },
  { key: LangKeys.cat_software, icon: <Laptop size={20} />, label: "Software" },
  { key: LangKeys.cat_furniture, icon: <Armchair size={20} />, label: "Furniture" },
  { key: LangKeys.cat_other, icon: <MoreHorizontal size={20} />, label: "Other" },
];

const AddExpenseView = ({ onBack, onSaved, initialExpense = null }) => {
  const { t } = useLang();
  const [expenseNumber, setExpenseNumber] = useState(initialExpense?.billNumber || "1");
  const [selectedDate, setSelectedDate] = useState(() => {
    if (initialExpense?.date) {
      return new Date(initialExpense.date);
    }
    return new Date();
  });
  const [amount, setAmount] = useState(initialExpense?.finalBillAmount?.toString() || "");
  const [selectedCategory, setSelectedCategory] = useState(initialExpense?.expenseCategory || null);
  const [paymentMode, setPaymentMode] = useState(initialExpense?.paymentType || 'cash');
  const [notes, setNotes] = useState(initialExpense?.privateNotes || "");
  const [photos, setPhotos] = useState(initialExpense?.photoUrls || []);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);

  const handleSave = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      alert(t(LangKeys.enter_amount));
      return;
    }
    if (!selectedCategory) {
      alert(t(LangKeys.select_expense_category));
      return;
    }

    const itemId = initialExpense?.items?.[0]?.item_id || initialExpense?.items?.[0]?.id || Date.now();
    const expensePayload = {
      id: initialExpense?.id,
      bill_type: 'expense',
      bill_number: expenseNumber,
      bill_date: formatLocalDate(selectedDate),
      party_id: 0,
      party_type: 'expense',
      customer_name: t(selectedCategory),
      customer_mobile: '',
      customer_gstin: '',
      customer_address: '',
      bill_items: [{
        item_id: itemId,
        item_name: t(selectedCategory),
        quantity: 1,
        unit_price: parseFloat(amount),
        original_price: parseFloat(amount),
        discount_value: 0,
        discount_type: 'rupee',
        tax_amount: 0,
        tax_rate: 0,
        gst_rate: '0%',
        gstRate: '0%',
        total: parseFloat(amount),
        cgst: 0,
        sgst: 0,
        igst: 0,
        tax_included: 0
      }],
      subtotal: parseFloat(amount),
      total_discount: 0,
      extra_discount: 0,
      total_tax: 0,
      cgst: 0,
      sgst: 0,
      igst: 0,
      round_off_amount: 0,
      total_bill_amount: parseFloat(amount),
      final_bill_amount: parseFloat(amount),
      payment_mode: paymentMode,
      payment_type: paymentMode,
      terms_n_conditions: '',
      private_note: notes,
      expense_category: selectedCategory,
      items: [{
        item_id: itemId,
        item_name: t(selectedCategory),
        quantity: 1
      }]
    };

    const result = initialExpense?.id
      ? await billService.updateBill(initialExpense.id, expensePayload)
      : await billService.addBill(expensePayload);
    if (result.success) {
      const serverBillId = result.bill_id || result.data?.id || result.bill?.id || expensePayload.id;
      const newPhotos = photos.filter(p => p && p.file);
      if (newPhotos.length > 0 && serverBillId) {
        for (const p of newPhotos) {
          try {
            await billService.uploadBillPhoto(p.file, serverBillId);
          } catch (uploadErr) {
            console.error("Failed to upload photo:", uploadErr);
          }
        }
      }
      if (onSaved) onSaved();
      onBack();
    } else {
      alert(result.message || "Failed to save expense");
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button onClick={onBack} style={styles.backBtn}><ArrowLeft size={20} /></button>
          <h3 style={styles.title}>{initialExpense ? t(LangKeys.edit_expense) : t(LangKeys.add_bill_expense_title)}</h3>
        </div>
      </div>

      <div style={styles.scrollContent}>
        {/* DATE & NUMBER */}
        <div style={styles.sectionCard}>
           <div style={styles.row}>
              <div style={styles.inputGroup}>
                 <span style={styles.label}>{t(LangKeys.add_bill_expense_no)}</span>
                 <div style={styles.inputWrapper}>
                    <input style={styles.rawInput} value={expenseNumber} onChange={(e) => setExpenseNumber(e.target.value)} />
                    <Edit2 size={14} color="#1D4ED8" />
                 </div>
              </div>
              <div style={styles.inputGroup}>
                 <span style={styles.label}>{t(LangKeys.date)}</span>
                 <div style={styles.inputWrapper}>
                    <Calendar size={16} color="#1D4ED8" />
                    <span style={styles.valText}>{formatDate(selectedDate)}</span>
                 </div>
              </div>
           </div>
        </div>

        {/* CATEGORY */}
        <div style={styles.sectionCard}>
           <span style={styles.label}>{t(LangKeys.category)}</span>
           {!selectedCategory ? (
             <div style={styles.emptySelector} onClick={() => setShowCategoryModal(true)}>
                <Grid size={24} color="#CBD5E1" />
                <span>{t(LangKeys.select_expense_category)}</span>
             </div>
           ) : (
             <div style={styles.selectedItem}>
                <div style={styles.itemIcon}><Grid size={20} color="#64748B" /></div>
                <div style={styles.itemInfo}>
                   <span style={styles.itemName}>{t(selectedCategory)}</span>
                   <button style={styles.editLink} onClick={() => setShowCategoryModal(true)}>{t(LangKeys.edit)}</button>
                </div>
                <button onClick={() => setSelectedCategory(null)} style={styles.removeBtn}><X size={18} /></button>
             </div>
           )}
           <button style={styles.textActionBtn} onClick={() => setShowCategoryModal(true)}>+ {t(LangKeys.select_category)}</button>
        </div>

        {/* AMOUNT */}
        <div style={styles.sectionCard}>
           <div style={styles.amountHeader}>
              <span style={{fontWeight: '800'}}>{t(LangKeys.expense_amount)}</span>
              <div style={styles.amountInputBox}>
                 <span style={styles.currencyPrefix}>₹</span>
                 <input 
                    placeholder="0.00" 
                    value={amount} 
                    onChange={(e) => setAmount(e.target.value)} 
                    style={styles.amountInput}
                 />
              </div>
           </div>
        </div>

        {/* PAYMENT MODE */}
        <div style={styles.sectionCard}>
           <span style={styles.label}>{t(LangKeys.add_bill_payment_details || 'Payment Mode')}</span>
           <div style={styles.chipGroup}>
              {['cash', 'online', 'unpaid'].map(mode => (
                <button 
                  key={mode} 
                  style={{...styles.chip, ...(paymentMode === mode ? styles.chipActive : {})}}
                  onClick={() => setPaymentMode(mode)}
                >
                  {paymentMode === mode && <Check size={14} />}
                  <span>{t(LangKeys[mode] || mode).toUpperCase()}</span>
                </button>
              ))}
           </div>
        </div>

        {/* NOTES & PHOTOS */}
        <div style={styles.sectionCard}>
           <span style={styles.label}>{t(LangKeys.add_bill_private_to_you)}</span>
            <div style={styles.notesBox} onClick={() => setShowNotesModal(true)}>
               <input 
                 placeholder={t(LangKeys.add_bill_add_notes_photos)} 
                 readOnly 
                 value={(() => {
                   if (notes && photos.length > 0) {
                     return `${notes} (${photos.length} photos)`;
                   }
                   if (notes) return notes;
                   if (photos.length > 0) {
                     return `${photos.length} photos`;
                   }
                   return '';
                 })()}
                 style={styles.notesPlaceholder}
               />
               <button style={styles.plusBtn}><Plus size={18} /></button>
            </div>
        </div>
      </div>

      <div style={styles.bottomBar}>
         <button style={styles.saveBtn} onClick={handleSave}>
            {initialExpense ? t(LangKeys.expense_updated_successfully || 'Update Expense') : t(LangKeys.save_expense || 'Save Expense')} {amount ? formatCurrency(amount) : ''}
         </button>
      </div>

      {/* CATEGORY MODAL */}
      {showCategoryModal && (
        <div style={styles.modalOverlay}>
           <div style={styles.modalContent}>
              <div style={styles.modalHeader}>
                 <h3>{t(LangKeys.select_category)}</h3>
                 <button onClick={() => setShowCategoryModal(false)}><X /></button>
              </div>
              <div style={styles.categoryGrid}>
                 {EXPENSE_CATEGORIES.map(cat => (
                   <div 
                      key={cat.key} 
                      style={{...styles.catItem, ...(selectedCategory === cat.key ? styles.catItemActive : {})}}
                      onClick={() => { setSelectedCategory(cat.key); setShowCategoryModal(false); }}
                   >
                      <div style={{...styles.catIconWrap, ...(selectedCategory === cat.key ? styles.catIconActive : {})}}>
                         {cat.icon}
                      </div>
                      <span style={styles.catLabel}>{t(cat.key)}</span>
                   </div>
                 ))}
              </div>
           </div>
        </div>
      )}

      {showNotesModal && (
        <AddNotesModal 
          isOpen={true}
          initialNotes={notes} 
          initialPhotos={photos}
          onSave={(n) => { 
            setNotes(n.notes); 
            setPhotos(n.photos); 
            setShowNotesModal(false); 
          }} 
          onClose={() => setShowNotesModal(false)} 
        />
      )}
    </div>
  );
};

const styles = {
  container: { display: 'flex', flexDirection: 'column', height: '100%', background: '#F3F4F6' },
  header: { padding: '20px 24px', background: 'white', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  backBtn: { width: '36px', height: '36px', borderRadius: '10px', border: 'none', background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  title: { fontSize: '18px', fontWeight: '800', margin: 0 },
  scrollContent: { flex: 1, padding: '14px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px' },
  sectionCard: { background: 'white', padding: '16px', borderRadius: '16px', border: '1px solid #E5E7EB' },
  row: { display: 'flex', gap: '12px' },
  inputGroup: { flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' },
  label: { fontSize: '12px', color: '#6B7280', fontWeight: '600' },
  inputWrapper: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', background: '#F9FAFB', borderRadius: '8px' },
  rawInput: { border: 'none', background: 'transparent', outline: 'none', fontWeight: '800', fontSize: '16px', width: '100%' },
  valText: { fontSize: '16px', fontWeight: '800' },
  emptySelector: { padding: '24px', border: '2px dashed #E5E7EB', borderRadius: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', color: '#9CA3AF', cursor: 'pointer', marginTop: '12px' },
  selectedItem: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0' },
  itemIcon: { width: '40px', height: '40px', background: '#F3F4F6', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  itemInfo: { flex: 1, display: 'flex', flexDirection: 'column' },
  itemName: { fontSize: '16px', fontWeight: '800' },
  editLink: { border: 'none', background: 'transparent', color: '#1D4ED8', fontWeight: '700', fontSize: '13px', textAlign: 'left', width: 'fit-content', padding: 0 },
  removeBtn: { border: 'none', background: 'transparent', color: '#9CA3AF' },
  textActionBtn: { border: 'none', background: 'transparent', color: '#1D4ED8', fontWeight: '800', fontSize: '13px', padding: '8px 0', marginTop: '4px' },
  amountHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  amountInputBox: { background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '8px' },
  currencyPrefix: { fontSize: '18px', fontWeight: '900' },
  amountInput: { border: 'none', background: 'transparent', outline: 'none', fontSize: '20px', fontWeight: '900', textAlign: 'right', width: '120px' },
  chipGroup: { display: 'flex', gap: '8px', marginTop: '12px' },
  chip: { flex: 1, height: '40px', borderRadius: '20px', border: '1px solid #E5E7EB', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '12px', fontWeight: '800', color: '#4B5563' },
  chipActive: { background: '#1D4ED8', color: 'white', borderColor: '#1D4ED8' },
  notesBox: { display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px' },
  notesPlaceholder: { flex: 1, padding: '12px', background: '#F9FAFB', border: 'none', borderRadius: '8px', outline: 'none', fontSize: '14px', cursor: 'pointer' },
  plusBtn: { width: '36px', height: '36px', borderRadius: '10px', background: '#F3F4F6', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  bottomBar: { padding: '16px 20px', background: 'white', borderTop: '1px solid #E5E7EB' },
  saveBtn: { width: '100%', height: '50px', background: '#1D4ED8', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '800', fontSize: '16px' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', zIndex: 1000 },
  modalContent: { width: '100%', background: 'white', borderTopLeftRadius: '24px', borderTopRightRadius: '24px', padding: '24px', maxHeight: '80vh', overflowY: 'auto' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  categoryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '16px' },
  catItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '12px', borderRadius: '12px' },
  catItemActive: { background: '#F3F4F6' },
  catIconWrap: { width: '48px', height: '48px', borderRadius: '12px', background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7280' },
  catIconActive: { background: '#1D4ED8', color: 'white' },
  catLabel: { fontSize: '12px', fontWeight: '700', textAlign: 'center', color: '#4B5563' }
};

export default AddExpenseView;
